import { useMemo } from "react"
import {
    useProjectsQuery,
    useTrashProjectsQuery,
    useCreateProjectMutation,
    useUpdateProjectMutation,
    useDeleteProjectMutation,
    useRestoreProjectMutation
} from "@/hooks/queries/use-projects"
import type { Project } from "@/types/project"

export function useProjects(viewMode: 'active' | 'completed' | 'trash' | 'all' = 'active') {
    const { data: allProjects = [], isLoading, refetch } = useProjectsQuery()
    const { data: trashProjects = [], isLoading: isLoadingTrash, refetch: refetchTrash } = useTrashProjectsQuery()
    const { mutateAsync: createProjectMutation } = useCreateProjectMutation()
    const { mutateAsync: updateProjectMutation } = useUpdateProjectMutation()
    const { mutateAsync: deleteProjectMutation } = useDeleteProjectMutation()
    const { mutateAsync: restoreProjectMutation } = useRestoreProjectMutation()

    // La query principal ya solo devuelve proyectos NO eliminados
    const activeAndCompletedProjects = useMemo(() => allProjects, [allProjects])

    // Filter projects based on viewMode
    const filteredProjects = useMemo(() => {
        if (viewMode === 'trash') {
            return trashProjects
        }

        if (viewMode === 'all') {
            return [...activeAndCompletedProjects, ...trashProjects]
        }

        // 'active' or 'completed' are filtered from the main projects list
        return activeAndCompletedProjects.filter(p => {
            if (viewMode === 'active') {
                return p.status === 'active' || !p.status
            }
            return p.status === viewMode
        })
    }, [activeAndCompletedProjects, trashProjects, viewMode])

    const createProject = async (name: string, color: string = '#3b82f6', hourlyRate: number = 0, clientId?: string | null) => {
        try {
            const newProject = await createProjectMutation({ name, color, hourlyRate, clientId })
            return {
                ...(newProject as Project),
                stats: { totalTasks: 0, completedTasks: 0, progress: 0, totalEstimatedHours: 0, totalWorkedHours: 0 }
            }
        } catch (error) {
            console.error("Error creating project:", error)
            return null
        }
    }

    const updateProject = async (projectId: string, updates: Partial<Project>) => {
        try {
            return await updateProjectMutation({ id: projectId, updates })
        } catch (error) {
            console.error("Error updating project:", error)
            return null
        }
    }

    const deleteProject = async (projectId: string, softDelete: boolean = true) => {
        try {
            await deleteProjectMutation({ id: projectId, softDelete })
            return true
        } catch (error) {
            console.error("Error deleting project:", error)
            return false
        }
    }

    const restoreProject = async (projectId: string) => {
        try {
            await restoreProjectMutation(projectId)
            return true
        } catch (error) {
            console.error("Error restoring project:", error)
            return false
        }
    }

    return {
        projects: filteredProjects,
        rawProjects: activeAndCompletedProjects,
        trashProjects,
        isLoading: isLoading || isLoadingTrash,
        createProject,
        updateProject,
        deleteProject,
        restoreProject,
        refresh: () => { refetch(); refetchTrash() }
    }
}
